import { HttpHandler, HttpInterceptor, HttpRequest, HttpEvent, HttpErrorResponse } from '@angular/common/http';
import { CustomModalComponent } from '@app/shared/ui/dialogs-modals-notifications/modal/contents/custom-modal.component';
import { Injectable, inject } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { catchError, take } from 'rxjs/operators';
import { MatDialog } from '@angular/material/dialog';
import { ResponseMessage } from '../config/response-message.config';
import { API_CONFIG } from '../config/api-config';
import { Location } from '@angular/common';
import { AuthStore } from '@app/store/auth.store';

@Injectable()
export class HttpErrorInterceptor implements HttpInterceptor {
    private authStore = inject(AuthStore);
    private location = inject(Location);
    dialog = inject(MatDialog);

    private readonly apiConfig = API_CONFIG;
    private readonly urlConfig = {
        urls: ['searchDetails', 'usageSummary', 'usageDetails'],
        urlsWithRedirect: [],
    };

    intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        return next.handle(req).pipe(
            catchError((errorResponse: HttpErrorResponse) => {
                const hasRedirect = this.urlHasRedirect(errorResponse.url);
                if (this.isUrlIncluded(errorResponse.url) || hasRedirect) {
                    this.promptErrorModal(hasRedirect);
                }
                return throwError(() => errorResponse);
            }),
        );
    }

    private isUrlIncluded(url: string): boolean {
        return Object.values(this.urlConfig).some((urlConfigValues) => {
            if (Array.isArray(urlConfigValues)) {
                return urlConfigValues.some((pathKey) => this.isPathInUrl(url, pathKey));
            }
        });
    }

    private urlHasRedirect(url: string): boolean {
        return this.urlConfig.urlsWithRedirect.some((urlValue) => {
            const apiString = this.apiConfig[urlValue];
            return url.includes(apiString);
        });
    }

    /**
     * Validates that the path is part of the URL
     *
     * @param url
     * @param pathKey
     * @param methodName must be an existing method in the class
     * @returns boolean
     */
    private isPathInUrl(url: string, pathKey: string, methodName?: string): boolean {
        try {
            const path = !methodName ? this.apiConfig[pathKey] : this.apiConfig[pathKey](this[methodName]());
            return url.includes(path);
        } catch {
            return false;
        }
    }

    private promptErrorModal(hasRedirect: boolean): void {
        const dialogRef = this.dialog.open(CustomModalComponent, {
            data: {
                type: 'warning',
                icon: 'fa-exclamation-circle',
                title: 'ERROR',
                content: ResponseMessage.ERROR_MESSAGES.UNEXPECTED_ERROR,
                yesLabel: 'Ok',
                hasNo: false,
            },
        });
        dialogRef
            .afterClosed()
            .pipe(take(1))
            .subscribe(() => {
                if (hasRedirect) {
                    this.location.back();
                }
            });
    }

    // Methods according global-error-config key
    // these methods will be called dynamically
    // -- ---------------------------------------
    private username(): string {
        let userName = this.authStore.userName() || '-';
        if (userName && userName.lastIndexOf('@') !== -1) {
            userName = userName.substring(0, userName.lastIndexOf('@'));
        }
        return userName;
    }

    // -- ---------------------------------------
}
